import { API, graphqlOperation } from 'aws-amplify';
import { onCreateNotice, onDeleteNotice, onUpdateNotice } from '../graphql/subscriptions';

const baseFields = `id title content attachments date createdAt updatedAt`;
const enhancedFields = `${baseFields} status deliveryTarget externalLink externalLinkLabel expiresAt isImportant`;

const LIST_ENHANCED = /* GraphQL */ `
  query AppListNotices($limit: Int, $nextToken: String) {
    listNotices(limit: $limit, nextToken: $nextToken) {
      items { ${enhancedFields} }
      nextToken
    }
  }
`;

const LIST_LEGACY = /* GraphQL */ `
  query AppListLegacyNotices($limit: Int, $nextToken: String) {
    listNotices(limit: $limit, nextToken: $nextToken) {
      items { ${baseFields} }
      nextToken
    }
  }
`;

const mutation = (operation, fields) => /* GraphQL */ `
  mutation App${operation}Notice($input: ${operation}NoticeInput!) {
    ${operation.toLowerCase()}Notice(input: $input) { ${fields} }
  }
`;

const run = async (query, variables = {}) => {
  const response = await API.graphql(graphqlOperation(query, variables));
  return response.data;
};

const missingPublishingFields = (error) => {
  const message = (error?.errors || []).map((item) => item.message).join(' ');
  return /status|deliveryTarget|externalLink|externalLinkLabel|expiresAt|isImportant|FieldUndefined/i.test(message);
};

const normalize = (notice) => ({
  ...notice,
  status: notice.status || 'PUBLISHED',
  deliveryTarget: notice.deliveryTarget || 'BOTH',
  isImportant: Boolean(notice.isImportant),
});

const listPages = async (query) => {
  const items = [];
  let nextToken = null;
  do {
    const data = await run(query, { limit: 250, nextToken });
    const page = data.listNotices;
    items.push(...(page?.items || []).filter(Boolean));
    nextToken = page?.nextToken || null;
  } while (nextToken);
  return items.map(normalize);
};

const cleanLegacyInput = ({ status, deliveryTarget, externalLink, externalLinkLabel, expiresAt, isImportant, ...input }) => input;

const save = async (operation, input) => {
  try {
    const data = await run(mutation(operation, enhancedFields), { input });
    return normalize(data[`${operation.toLowerCase()}Notice`]);
  } catch (error) {
    if (!missingPublishingFields(error)) throw error;
    const requiresPublishingSchema = input.status === 'DRAFT'
      || input.status === 'ARCHIVED'
      || (input.deliveryTarget && input.deliveryTarget !== 'BOTH')
      || input.externalLink
      || input.externalLinkLabel
      || input.expiresAt
      || input.isImportant;
    if (requiresPublishingSchema) {
      throw new Error('Deploy the updated Notice schema before saving draft, expiration, or important settings.');
    }
    const data = await run(mutation(operation, baseFields), { input: cleanLegacyInput(input) });
    return normalize(data[`${operation.toLowerCase()}Notice`]);
  }
};

export const isNoticeActive = (notice, now = new Date()) => {
  if ((notice.status || 'PUBLISHED') !== 'PUBLISHED') return false;
  const expiresAt = notice.expiresAt ? new Date(notice.expiresAt) : null;
  if (expiresAt && !Number.isNaN(expiresAt.getTime()) && expiresAt <= now) return false;
  const eventDate = notice.date ? new Date(notice.date) : null;
  if (!expiresAt && eventDate && !Number.isNaN(eventDate.getTime()) && eventDate < now) return false;
  return true;
};

export const isNoticeForChannel = (notice, channel) => {
  const deliveryTarget = notice.deliveryTarget || 'BOTH';
  return deliveryTarget === 'BOTH' || deliveryTarget === channel;
};

export const sortActiveNotices = (items) => [...items].sort((a, b) => {
  if (Boolean(a.isImportant) !== Boolean(b.isImportant)) return a.isImportant ? -1 : 1;
  const aDate = a.date ? new Date(a.date).getTime() : Number.MAX_SAFE_INTEGER;
  const bDate = b.date ? new Date(b.date).getTime() : Number.MAX_SAFE_INTEGER;
  if (aDate !== bDate) return aDate - bDate;
  return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
});

export const noticeApi = {
  listAll: async () => {
    try {
      return await listPages(LIST_ENHANCED);
    } catch (error) {
      if (!missingPublishingFields(error)) throw error;
      return listPages(LIST_LEGACY);
    }
  },
  listActive: async () => sortActiveNotices((await noticeApi.listAll()).filter((notice) => isNoticeActive(notice))),
  listActiveFor: async (channel) => sortActiveNotices((await noticeApi.listAll())
    .filter((notice) => isNoticeActive(notice) && isNoticeForChannel(notice, channel))),
  watch: (onChange, onError = () => {}) => {
    const subscriptions = [onCreateNotice, onUpdateNotice, onDeleteNotice]
      .map((query) => {
        try {
          const observable = API.graphql(graphqlOperation(query));
          if (typeof observable?.subscribe !== 'function') return null;
          return observable.subscribe({ next: onChange, error: onError });
        } catch (error) {
          onError(error);
          return null;
        }
      })
      .filter(Boolean);
    return () => subscriptions.forEach((subscription) => subscription.unsubscribe());
  },
  create: (input) => save('Create', input),
  update: (input) => save('Update', input),
  delete: async (id) => {
    const data = await run(mutation('Delete', enhancedFields), { input: { id } }).catch(async (error) => {
      if (!missingPublishingFields(error)) throw error;
      return run(mutation('Delete', baseFields), { input: { id } });
    });
    return data.deleteNotice;
  },
};
